import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useFinanceGateQueue, useReadyToInvoice, useCustomerInvoices, useJobs } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, FilePlus, Check, Loader2 } from "lucide-react";
import { BusinessStage, financeReviewStage, stageForInvoice } from "@/components/shared/BusinessStage";
import { FinanceCustomerContext } from "@/components/finance/FinanceCustomerContext";

// Aug 2026 Finance dashboard scope pass — replaces the separate Work Queue,
// Supplier Invoice, Supplier Directory and Invoicing & AR nav tabs with a
// single Finance Pipeline, mirroring the visual language of the Sales
// Pipeline (frontend/src/pages/sales/Pipeline.jsx: Column/Card layout,
// PageHeader conventions) but with Finance-specific stages. Both buckets
// reuse existing, already-live data sources unchanged:
//   - "Credit Check / Finance Review" = GET /finance-gate/queue (previously
//     rendered by Credit.jsx, kept as-is at /finance/credit for deep links).
//   - "Invoice Clearance / Final Invoice Preparation" = GET
//     /finance/ready-to-invoice (previously rendered by Receivables.jsx,
//     kept as-is at /finance/receivables for deep links). The
//     prepare-draft -> approve-and-issue flow is the SAME two backend calls
//     Receivables.jsx already used (POST /finance/final-invoice/draft and
//     /finance/final-invoice/approve); it is reproduced here inline as a
//     dialog rather than navigating away, because for this bucket "opening
//     the case" and "performing the required Finance action" are the same
//     step — issuing the final invoice. No backend route was added or
//     changed.
//
// Round 2 (Payment Pending stage + shared customer context) — a third
// bucket, "Payment Pending / Awaiting Payment" = GET /finance/customer-invoices
// (previously rendered by Receivables.jsx, kept as-is at /finance/receivables
// for deep links), filtered to OPEN / OVERDUE / PARTIALLY PAID exactly the
// way Receivables.jsx already does (statusKey/outstandingFor below are the
// same helpers, copied rather than imported since Receivables.jsx doesn't
// export them). "Next follow-up / required action" is a computed string
// from the real due date (there is no backend reminder/follow-up field
// anywhere in this system); "Last follow-up date" has no real backing data
// at all, so it is intentionally NOT rendered rather than fabricated.
//
// Round 3 (Aug 2026) — clicking a Payment Pending card used to open an
// inline payment dialog (built in Round 2, embedding <FinanceCustomerContext>
// for customer/document context). Per this round's product request, it now
// navigates straight to the Finance Customer Profile
// (/finance/customers/{customerId} → Customer360.jsx) instead — the record-
// payment / send-reminder logic that dialog held has moved to Customer360's
// new "Payment & Outstanding" section (finance-only,
// frontend/src/components/finance/FinanceProfileSections.jsx), reusing the
// exact same POST /finance/customer-invoices/{id}/payment endpoint. The
// dialog itself was removed rather than kept as a redundant second path.
//
// The Invoice Clearance dialog below still embeds <FinanceCustomerContext>,
// the one shared customer-info + documents panel also used by
// FinanceOrderReview.jsx for the Credit Check case — see
// frontend/src/components/finance/FinanceCustomerContext.jsx.
const statusKey = (value) => String(value || "").trim().toUpperCase().replaceAll("_", " ");
const outstandingFor = (invoice) => Number(
  invoice?.outstandingAmount ?? invoice?.outstanding ?? invoice?.balance ??
  Math.max(Number(invoice?.amount || 0) - Number(invoice?.paidAmount || 0), 0),
);
const parseDate = (label) => {
  if (!label) return null;
  const d = new Date(label);
  return Number.isNaN(d.getTime()) ? null : d;
};
const followUpAction = (invoice) => {
  const status = statusKey(invoice.status);
  if (status === "OVERDUE") {
    const due = parseDate(invoice.due);
    const days = due ? Math.round((Date.now() - due.getTime()) / 86400000) : null;
    return days != null && days > 0 ? `Overdue by ${days} day${days === 1 ? "" : "s"} — follow up` : "Overdue — follow up";
  }
  if (status === "PARTIALLY PAID") return `Partially paid — ${money(outstandingFor(invoice), invoice.currency || "AED")} remaining, follow up for balance`;
  const due = parseDate(invoice.due);
  if (due) {
    const days = Math.round((due.getTime() - Date.now()) / 86400000);
    if (days >= 0) return `Due in ${days} day${days === 1 ? "" : "s"}`;
  }
  return "Awaiting payment";
};

function Column({ title, hint, count, children, empty }) {
  return (
    <div className="flex flex-col w-[360px] shrink-0">
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-sm text-slate-700">{title}</span>
          <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-1.5">{count}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{hint}</span>
      </div>
      <div className="flex-1 space-y-2">{count === 0 ? <div className="text-[12px] text-slate-400 px-1 py-3">{empty}</div> : children}</div>
    </div>
  );
}

function Card({ onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition-all">{children}</button>;
}

const creditAction = (row) => {
  const status = String(row.financeGateStatus || "PENDING").toUpperCase();
  if (status === "ON_HOLD") return "Resolve finance hold before this order can proceed";
  if (status.includes("EXCEPTION")) return "Awaiting management exception decision";
  if (status === "PENDING" || !row.financeGateStatus) return "Run finance gate evaluation — credit check & approval decision required";
  return "Review finance decision and release for Operations";
};

const invoiceAction = (row) => (row.invoiceEligible ? "Prepare and issue final invoice" : "Resolve blockers before invoicing");
const blockerMessage = (blocker) => (typeof blocker === "string" ? blocker : blocker?.message || blocker?.code || "Invoice preparation is blocked.");
const blockersFor = (row) => (Array.isArray(row?.blockers) ? row.blockers.map(blockerMessage) : row?.blocker ? [blockerMessage(row.blocker)] : []);

export default function FinancePipeline() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: creditQueue, isLoading: loadingCredit } = useFinanceGateQueue();
  const { data: rti, isLoading: loadingRti } = useReadyToInvoice();
  const { data: invoices, isLoading: loadingInvoices } = useCustomerInvoices();
  const { data: jobs, isLoading: loadingJobs } = useJobs();

  const [draft, setDraft] = useState(null);
  const [draftingJobId, setDraftingJobId] = useState(null);
  const [approving, setApproving] = useState(false);

  if (loadingCredit || loadingRti || loadingInvoices || loadingJobs || !creditQueue || !rti || !invoices || !jobs) return <Loading />;

  const openDraft = async (jobId) => {
    setDraftingJobId(jobId);
    setDraft(null);
    try {
      const { data } = await api.post("/finance/final-invoice/draft", { jobId });
      setDraft({ ...data, total: Number(data.revenue ?? 0) });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Final invoice draft could not be prepared."));
    } finally {
      setDraftingJobId(null);
    }
  };

  const approve = async () => {
    if (!draft) return;
    setApproving(true);
    try {
      await api.post("/finance/final-invoice/approve", {
        jobId: draft.jobId,
        total: Number(draft.total),
        lineItems: draft.lineItems,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["rti"] }),
        qc.invalidateQueries({ queryKey: ["ar"] }),
        qc.invalidateQueries({ queryKey: ["profit"] }),
      ]);
      toast.success("Final invoice approved and issued");
      setDraft(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Final invoice could not be issued."));
    } finally {
      setApproving(false);
    }
  };

  const paymentPending = invoices.filter((inv) => ["OPEN", "OVERDUE", "PARTIALLY PAID"].includes(statusKey(inv.status)));
  // "Sent to Operations" — real Jobs created via the Finance Gate handoff
  // (POST /finance-gate/acceptances/{id}/create-job), never a fabricated
  // list. `acceptanceId` is only ever set on Jobs created through that exact
  // path, which is what distinguishes a genuine Finance→Operations handoff
  // from a legacy/seed Job with no Finance chain behind it.
  const sentToOps = jobs.filter((j) => !!j.acceptanceId).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const totalCases = creditQueue.length + rti.length + paymentPending.length + sentToOps.length;

  return (
    <>
      <PageHeader title="Finance pipeline" subtitle="Every customer case currently waiting on Finance, grouped by stage" />
      <div className="flex-1 overflow-auto p-6">
        <div className="flex gap-4 min-w-max">
          <Column
            title="Credit Check / Finance Review"
            hint="Awaiting Finance decision"
            count={creditQueue.length}
            empty="Nothing awaiting Finance review — every accepted order has a decision or has moved to Operations."
          >
            {creditQueue.map((row) => (
              <Card key={row.acceptanceId} testid={`fpipe-credit-${row.acceptanceId}`} onClick={() => navigate(`/finance/orders/${row.acceptanceId}`)}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm text-slate-800 truncate">{row.customer}</span>
                  <StatusPill status={row.financeGateStatus || "Pending"} />
                </div>
                <div className="text-[12px] text-slate-500 mb-1">{row.quotationId} V{row.quotationVersion} · {money(row.acceptedAmount, row.currency)}</div>
                <div className="text-[11px] font-medium text-indigo-600">{creditAction(row)}</div>
                {row.reason && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 truncate">{row.reason}</div>}
                <div className="mt-2"><BusinessStage stage={financeReviewStage(row.financeGateStatus || "Finance review pending")} compact /></div>
              </Card>
            ))}
          </Column>

          <Column
            title="Invoice Clearance / Final Invoice Preparation"
            hint="Ready for final invoicing"
            count={rti.length}
            empty="No jobs are currently ready for final invoicing."
          >
            {rti.map((row) => {
              const blockers = blockersFor(row);
              const eligible = row.invoiceEligible === true;
              const drafting = draftingJobId === row.jobId;
              return (
                <Card key={row.jobId} testid={`fpipe-rti-${row.jobId}`} onClick={() => (eligible ? openDraft(row.jobId) : navigate(`/finance/jobs/${row.jobId}`))}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm text-slate-800 truncate">{row.customer}</span>
                    <StatusPill status={eligible ? "Ready" : "Blocked"} bucket={eligible ? "success" : "warning"} />
                  </div>
                  <div className="text-[12px] text-slate-500 mb-1">Job {row.jobId} · Revenue {money(row.revenue, row.currency || "AED")} · GP {money(row.gp, row.currency || "AED")} ({row.gpPct}%)</div>
                  <div className="text-[11px] font-medium text-indigo-600 flex items-center gap-1">
                    {drafting && <Loader2 className="h-3 w-3 animate-spin" />}{invoiceAction(row)}
                  </div>
                  {!eligible && blockers.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {blockers.map((message, index) => (
                        <div key={`${message}-${index}`} className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 truncate">{message}</div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2"><BusinessStage stage={stageForInvoice({ status: "OPEN" })} compact /></div>
                </Card>
              );
            })}
          </Column>

          <Column
            title="Sent to Operations"
            hint="Job created, handed off"
            count={sentToOps.length}
            empty="No cases have been handed off to Operations yet."
          >
            {sentToOps.map((job) => (
              <Card key={job.id} testid={`fpipe-job-${job.id}`} onClick={() => navigate(`/finance/jobs/${job.id}`)}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm text-slate-800 truncate">{job.customer}</span>
                  <StatusPill status={job.status} />
                </div>
                <div className="text-[12px] text-slate-500 mb-1">Job {job.id} · {job.mode || "—"}</div>
                <div className="text-[12px] text-slate-600 mb-1 truncate">{job.lane || "—"}</div>
                <div className="text-[11px] text-slate-500">Sent {(job.createdAt || "").slice(0, 10) || "—"} by {job.createdBy || "—"}</div>
                <div className="mt-2"><BusinessStage stage={job.businessStage} compact /></div>
              </Card>
            ))}
          </Column>

          <Column
            title="Payment Pending / Awaiting Payment"
            hint="Invoice issued, payment outstanding"
            count={paymentPending.length}
            empty="No customer invoices are currently open for collection."
          >
            {paymentPending.map((inv) => {
              const overdue = statusKey(inv.status) === "OVERDUE";
              return (
                <Card key={inv.id} testid={`fpipe-pay-${inv.id}`} onClick={() => navigate(`/finance/customers/${inv.customerId}`)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm text-slate-800 truncate">{inv.customer}</span>
                    <StatusPill status={inv.status} />
                  </div>
                  <div className="text-[12px] text-slate-500 mb-1">{inv.id} · Job {inv.jobId}</div>
                  <div className="text-[12px] text-slate-600 mb-1">Invoice {money(inv.amount, inv.currency || "AED")} · Outstanding <span className="font-semibold text-slate-900">{money(outstandingFor(inv), inv.currency || "AED")}</span></div>
                  <div className="text-[11px] text-slate-500 mb-1">Issued {inv.issued || "—"} · Due {inv.due || "—"}</div>
                  <div className={`text-[11px] font-medium ${overdue ? "text-red-600" : "text-indigo-600"}`}>{followUpAction(inv)}</div>
                  <div className="mt-2"><BusinessStage stage={inv.businessStage || stageForInvoice(inv)} compact /></div>
                </Card>
              );
            })}
          </Column>
        </div>
        {totalCases === 0 && <EmptyState icon={ShieldCheck} title="Nothing in the Finance pipeline right now" sub="Cases will appear here as Sales sends orders for finance review, jobs become ready for final invoicing, and issued invoices await payment." />}
      </div>

      <Dialog open={!!draft} onOpenChange={(open) => { if (!open && !approving) setDraft(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="final-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FilePlus className="h-4 w-4 text-blue-800" />Final invoice · {draft?.customer}</DialogTitle>
            <DialogDescription>Review the approved commercial value and Operations actuals before issuing the invoice.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              {draft.aiNote && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><div className="mb-1 text-xs font-semibold text-slate-900">Draft review note</div>{draft.aiNote}</div>}
              <div className="grid grid-cols-3 gap-3 text-center">
                <Metric label="Quoted sell" value={money(draft.revenue, draft.currency || "AED")} />
                <Metric label="Actual cost" value={money(draft.actualCost, draft.currency || "AED")} muted />
                <Metric label="Margin" value={`${draft.marginPct}%`} accent />
              </div>
              <div>
                <label htmlFor="fpipe-final-invoice-total" className="text-xs font-semibold text-slate-700">Invoice total ({draft.currency || "AED"})</label>
                <Input id="fpipe-final-invoice-total" type="number" value={draft.total} readOnly aria-readonly="true" data-testid="final-invoice-total" className="mt-1 bg-slate-50 font-mono-num text-slate-700" />
                <div className="text-[11px] text-slate-500 mt-1">Locked to approved quotation {draft.quotationId || "—"}. Revise the quotation before invoicing if the commercial total is incorrect.</div>
              </div>

              {/* Same shared customer-info + documents workspace as the Credit
                  Check case — Finance verifies the customer and supporting
                  documents from right here before issuing the invoice. */}
              <FinanceCustomerContext clientId={draft.customerId} defaultOpen={false} />

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDraft(null)} disabled={approving} data-testid="final-invoice-cancel">Cancel</Button>
                <Button onClick={approve} disabled={approving} data-testid="final-invoice-approve">
                  {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Approve and issue
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const Metric = ({ label, value, accent, muted }) => <div className="rounded-md bg-slate-50 border border-slate-200 py-2"><div className={`font-display font-bold text-base font-mono-num ${accent ? "text-emerald-700" : muted ? "text-slate-500" : "text-slate-900"}`}>{value}</div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div></div>;
